import type { TimeEntryParsePreview } from '@fos/shared/contracts/time';
import { todayLocal } from '@fos/shared/time';
import { keepPreviousData } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatDuration, formatMoneyAuto, formatRate } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Textarea } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { ClientDot } from '../../clients/components/ClientDot';
import { showWarnings, timeErrorToast, useInvalidateTime } from '../hooks/useTimeMutations';
import { chipDay } from '../time-format';

/**
 * Wpis zdaniem albo głosem (MVP §17 nr 8, Figma 06 „Mobile, wpis głosem” 3021:2690): zdanie → podgląd z serwera
 * (`time.parse`, nic nie zapisuje) → przy niejednoznacznym kliencie wybór „Który klient?” → „Zapisz” (`time.create`).
 * „Wpisz ręcznie” przenosi rozpoznane wartości do zwykłego formularza.
 *
 * Zapisuje się tylko podgląd BIEŻĄCEGO zdania: dopóki podgląd nie odpowiada wpisanemu tekstowi (debounce, zapytanie
 * w toku, poprzedni wynik pokazywany w trakcie), „Zapisz” jest wyłączone, a Enter tylko odświeża podgląd. Wpis przez
 * północ wymaga jawnego „Koniec następnego dnia” (MVP §7), jak w formularzu.
 */

const CLIENT_CODES = new Set(['client_missing', 'client_unknown', 'client_ambiguous']);

/** Wartość z opóźnieniem + `flush` (natychmiast bieżąca wartość, np. Enter przed upływem opóźnienia). */
function useDebounced<T>(value: T, ms: number): [T, () => void] {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return [v, () => setV(value)];
}

export function SentenceEntry({
  initialText = '',
  source = 'manual',
  autoFocus = true,
  onSaved,
  onManual,
  onCancel,
}: {
  initialText?: string;
  /** „voice” dla tekstu z dyktowania */
  source?: 'manual' | 'voice';
  autoFocus?: boolean;
  onSaved?: () => void;
  /** „Wpisz ręcznie” z rozpoznanymi wartościami (albo bez, gdy nic nie rozpoznano) */
  onManual?: (preview: TimeEntryParsePreview | null) => void;
  onCancel?: () => void;
}) {
  const [text, setText] = useState(initialText);
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [endNextDay, setEndNextDay] = useState(false);
  const [debounced, flush] = useDebounced(text.trim(), 350);
  const enabled = debounced.length >= 3;
  const parse = trpc.time.parse.useQuery(
    { text: debounced, ...(clientId ? { clientId } : {}), ...(endNextDay ? { confirmEndNextDay: true } : {}) },
    { enabled, placeholderData: keepPreviousData, staleTime: 30_000, retry: false },
  );
  const create = trpc.time.create.useMutation();
  const invalidate = useInvalidateTime();
  const groupName = useId();
  const today = todayLocal();

  // nowe zdanie = nowy wybór klienta i dnia końca
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset wyboru tylko przy zmianie tekstu
  useEffect(() => {
    setClientId(undefined);
    setEndNextDay(false);
  }, [debounced]);

  const preview = enabled ? parse.data : undefined;
  const candidates = preview?.candidates ?? [];
  const otherIssues = (preview?.ambiguities ?? []).filter((a) => !CLIENT_CODES.has(a.code) || candidates.length === 0);
  const needsEndDay = Boolean(preview?.ambiguities.some((a) => a.code === 'crosses_midnight')) && !endNextDay;
  // podgląd odpowiada temu, co jest wpisane (nie poprzedniemu zdaniu w trakcie pisania)
  const current = enabled && debounced === text.trim() && !parse.isPlaceholderData && !parse.isFetching;
  const canSave = current && Boolean(preview?.draft);

  const save = async () => {
    if (!canSave || !preview?.draft) return;
    try {
      const result = await create.mutateAsync({ ...preview.draft, source });
      await invalidate();
      showWarnings(result.warnings);
      if (result.warnings.length === 0) {
        // toast sukcesu tylko bez ostrzeżeń (jedno powiadomienie naraz); godziny, żeby było widać, co zapisano
        const hours = `${result.entry.start}–${result.entry.end}${result.entry.endDate ? ' (+1)' : ''}`;
        toast.success('Dodano czas', {
          description: [hours, result.entry.description].filter(Boolean).join(' · '),
        });
      }
      setText('');
      onSaved?.();
    } catch (err) {
      timeErrorToast(err);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Textarea
        aria-label="Zdanie z czasem pracy"
        autoFocus={autoFocus}
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // Enter tuż po edycji: najpierw podgląd bieżącego zdania (bez zapisu poprzedniego)
            if (canSave) void save();
            else if (debounced !== text.trim()) flush();
          }
        }}
        placeholder="np. „REA 1h40 poprawki kontaktu od 10:20”"
        maxLength={500}
        className="text-body-m"
      />

      {enabled && parse.isPending && (
        <div role="status" aria-label="Rozpoznawanie" className="flex flex-col gap-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3.5 w-1/2" />
        </div>
      )}
      {enabled && parse.isError && (
        <FormMessage>{errorMessage(parse.error, 'Nie udało się odczytać zdania.')}</FormMessage>
      )}

      {preview && candidates.length > 0 && (
        <fieldset className="flex flex-col">
          <legend className="mb-2 text-body-s text-fg-muted">Który klient?</legend>
          {candidates.map((c) => {
            const checked = (clientId ?? preview.client?.id) === c.id;
            return (
              <label
                key={c.id}
                className="flex min-h-12 cursor-pointer items-center gap-3 border-b border-line has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-accent"
              >
                <input
                  type="radio"
                  name={groupName}
                  value={c.id}
                  checked={checked}
                  onChange={() => setClientId(c.id)}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  className={cn(
                    'inline-flex size-5 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                    checked ? 'border-accent' : 'border-line-strong',
                  )}
                >
                  {checked && <span className="size-2.5 rounded-full bg-accent" />}
                </span>
                <ClientDot color={c.color} />
                <span className="min-w-0 flex-1 truncate text-body-s text-fg">{c.name}</span>
                {c.defaultRate && (
                  <span className="shrink-0 text-body-s text-fg-muted">{formatRate(c.defaultRate, c.currency)}</span>
                )}
              </label>
            );
          })}
        </fieldset>
      )}

      {preview && (
        <div className="flex flex-col gap-1" aria-live="polite">
          <Mono className="text-body-m text-fg">
            {preview.start ?? '—'} – {preview.end ?? '—'}
            {preview.endDate ? ' (+1)' : ''}
            {preview.durationSeconds ? ` · ${formatDuration(preview.durationSeconds)}` : ''}
          </Mono>
          <p className="text-caption text-fg-muted">
            {[
              chipDay(preview.date, today),
              preview.client ? preview.client.name : null,
              preview.project?.name ?? null,
              preview.description || null,
              preview.amount !== undefined && preview.billable
                ? formatMoneyAuto(preview.amount, preview.currency)
                : null,
              preview.client && !preview.billable ? 'nierozliczany' : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {otherIssues.length > 0 && (
            <ul className="mt-2 flex flex-col gap-1">
              {otherIssues.map((a) => (
                <li key={a.code} className="flex items-start gap-1.5 text-caption text-warning">
                  <TriangleAlert size={14} strokeWidth={1.75} aria-hidden className="mt-px shrink-0" />
                  <span>{a.message}</span>
                </li>
              ))}
            </ul>
          )}
          {needsEndDay && (
            <div className="mt-1">
              <Button size="s" variant="secondary" onClick={() => setEndNextDay(true)}>
                Koniec następnego dnia
              </Button>
            </div>
          )}
          {endNextDay && preview.endDate && (
            <p className="mt-1 flex flex-wrap items-center gap-2 text-caption text-fg-muted">
              Koniec następnego dnia ({chipDay(preview.endDate, today)}).
              <button
                type="button"
                className="rounded-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                onClick={() => setEndNextDay(false)}
              >
                Cofnij
              </button>
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Anuluj
          </Button>
        )}
        {onManual && (
          <Button variant="secondary" onClick={() => onManual(preview ?? null)}>
            Wpisz ręcznie
          </Button>
        )}
        <Button variant="primary" onClick={() => void save()} disabled={!canSave} loading={create.isPending}>
          Zapisz
        </Button>
      </div>
    </div>
  );
}
